<template>
  <div class="mega-menu bg-workbook-white" :class="{ 'active': menuOpen }">
    <UContainer class="flex min-h-[inherit] items-stretch ">
      <!-- Left menu : generated from mainnav.yml -->
      <div
        class=" relative pr-4 mr-12 w-3/12 flex flex-col self-stretch min-h-[inherit] pt-5 bg-workbook-orange text-white before:absolute before:inset-y-0 before:right-full before:w-screen before:bg-workbook-orange">
        <ul class="flex flex-1 flex-col gap-6 py-8 justify-center">
          <template v-for="item in mainMenuItems" :key="item.label">
            <li v-if="!item.login || item.login && auth.isAuthenticated" class="gap-2 flex">
              <NuxtLink :to="item.to"
                class="text-xl hover:underline decoration-2 underline-offset-5 cursor-pointer text-white flex items-center gap-2"
                @click="emitClose">
                {{ item.label }}

                <Icon v-if="item.icon" :name="item.icon" />
              </NuxtLink>
            </li>
          </template>
        </ul>
        <div class="close-menu mt-auto pb-8">
          <UButton size="sm" icon="workbook-x" variant="link"
            class="group mb-8 ml-4 text-xl text-white hover:text-white pl-0 m-0" @click="emitClose">
            Close Menu
          </UButton>
        </div>

      </div>
      <!-- Desktop: Horizontal and vertical tabs -->
      <div class="w-9/12 flex flex-col pt-12">

        <div class="flex gap-5">
          <h3 class="text-base">
            Workbook Demo Menu
          </h3>
          <p class="text-sm">
            Try out the features and intuitive simplicity of the Kudo Sales<br />
            B2B Workbook. Pick some styles and make a test order.
          </p>
        </div>




        <UTabs :items="mainTabs" size="lg" variant="link">
          <template #content="{ item }">
            <div class="flex items-start w-full border-t border-black/30">
              <UTabs :items="categoryTabs[item.key]" orientation="vertical" size="md" variant="link"
                class="min-w-[220px] items-start">
                <template #content="{ item: catItem }">
                  <div class="pt-6 w-full">
                    <div class="grid grid-cols-2 gap-x-4 w-full mt-2">
                      <div class="col-span-2 mb-6">
                        <NuxtLink :to="productUrl(catItem.slug, item.key)"
                          class="uppercase flex items-center gap-2 mb-3 hover:underline cursor-pointer text-(--workbook-base-font-color)"
                          @click="emitClose">
                          <UButton class="p-0  uppercase font-normal text-base" size="md" variant="link" v-if="item.key === 'All'"
                            trailing-icon="workbook:arrow-right">
                            See All {{ catItem.label }}
                          </UButton>
                          <UButton class="p-0  uppercase font-normal text-base" size="md" variant="link" v-else
                            trailing-icon="workbook:arrow-right">
                            See All {{ item.label }} {{ catItem.label }}
                          </UButton>
                        </NuxtLink>
                      </div>
                      <template v-if="hasBothGenders(catItem)">
                        <div class="flex flex-col">
                          <div class="mb-5 font-semibold">Men’s</div>
                          <div v-for="prod in catItem.products.Male" :key="prod.Style" class="mb-4">
                            <NuxtLink :to="productUrl(catItem.slug, item.key) + `#style-${prod.Style}`"
                              class="font-normal hover:underline cursor-pointer text-(--workbook-base-font-color)"
                              @click="emitClose">{{ prod.name }}</NuxtLink>
                          </div>
                        </div>
                        <div class="flex flex-col">
                          <div class="font-semibold mb-5">Women’s</div>
                          <div v-for="prod in catItem.products.Female" :key="prod.Style" class="mb-4">
                            <NuxtLink :to="productUrl(catItem.slug, item.key) + `#style-${prod.Style}`"
                              class="font-normal hover:underline cursor-pointer text-[color:var(--workbook-base-font-color)]"
                              @click="emitClose">{{ prod.name }}</NuxtLink>
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <div class="flex flex-col col-span-2">
                          <div v-if="hasSingleGender(catItem)" class="font-normal mb-2">
                            {{ singleGenderLabel(catItem) }}
                          </div>
                          <div v-for="prod in catItem.products.All" :key="prod.Style" class="mb-1">
                            <NuxtLink :to="productUrl(catItem.slug, item.key) + `#style-${prod.Style}`"
                              class="font-normal hover:underline cursor-pointer text-[color:var(--workbook-base-font-color)]"
                              @click="emitClose">{{ prod.name }}</NuxtLink>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </template>
              </UTabs>
            </div>
          </template>
        </UTabs>
      </div>

    </UContainer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useCatalogStore } from '@/store/catalog'
import { useWindowSize } from '@vueuse/core'
import { useRouter, useRoute } from 'vue-router'

import { useAuthStore } from '@/store/auth'

const auth = useAuthStore()
const props = defineProps<{ menuOpen: boolean }>()
const emit = defineEmits(['close'])

const catalog = useCatalogStore()

function buildCategoryTabs(cats) {
  return cats.map(cat => {
    const male = cat.ProductModels.filter(m => m.Gender === 'Male')
    const female = cat.ProductModels.filter(m => m.Gender === 'Female')
    const all = cat.ProductModels.filter(m => !m.Gender || (m.Gender !== 'Male' && m.Gender !== 'Female'))

    let allProducts = []
    if (male.length && !female.length) allProducts = male
    else if (female.length && !male.length) allProducts = female
    else if (all.length) allProducts = all

    return {
      label: cat.Name,
      slug: cat.Slug,
      products: {
        Male: male,
        Female: female,
        All: allProducts
      }
    }
  })
}

function productUrl(slug: string, modeKey: string) {
  return `/products/${slug}${modeKey !== 'All' ? '?category=' + encodeURIComponent(modeKey) : ''}`
}

const categoryTabs = computed<Record<string, ReturnType<typeof buildCategoryTabs>>>(() => {
  const tabs: Record<string, ReturnType<typeof buildCategoryTabs>> = {
    All: buildCategoryTabs(catalog.categories)
  }
  for (const topCat of catalog.topCategories) {
    tabs[topCat] = buildCategoryTabs(catalog.categoriesByTopCategory(topCat))
  }
  return tabs
})
const mainTabs = computed(() => {
  const tabs = [{ key: 'All', label: 'All' }]
  for (const cat of catalog.topCategories) {
    tabs.push({ key: cat, label: cat })
  }
  return tabs
})

function hasBothGenders(catItem) {
  return catItem.products.Male.length && catItem.products.Female.length
}
function hasSingleGender(catItem) {
  return (
    (catItem.products.Male.length && !catItem.products.Female.length) ||
    (!catItem.products.Male.length && catItem.products.Female.length)
  )
}
function singleGenderLabel(catItem) {
  if (catItem.products.Male.length && !catItem.products.Female.length) return "Men’s"
  if (catItem.products.Female.length && !catItem.products.Male.length) return "Women’s"
  return ""
}

const { width } = useWindowSize()
const isMobile = computed(() => width.value < 767)

const mainTabIdx = ref(0)
const categoryView = ref(null)
function openCategory(cat) {
  categoryView.value = cat
}
function goBack() {
  if (categoryView.value) categoryView.value = null
  else mainTabIdx.value = 0
}

// Hämta mainnav-data från Nuxt Content
const { data: mainnav } = await useAsyncData('mainnav', () =>
  queryCollection('mainnav').first()
)
const mainMenuItems = computed(() => {
  const menu = Array.isArray(mainnav.value?.menuitems) ? mainnav.value.menuitems : []
  return menu.filter(item => item && item.label)
})

// Emit close event on link click
function emitClose() {
  emit('close')
}

// Bevaka router och stäng menyn automatiskt vid navigation
const router = useRouter()
const route = useRoute()
watch(
  () => route.fullPath,
  () => {
    if (props.menuOpen) emit('close')
  }
)
</script>

<style scoped>
@reference "../../assets/css/main.css";

.mega-menu {
  transition: all 0.3s var(--ease-snappy);
  transform: translateY(-100%);
  opacity: 0;
}

.mega-menu.active {
  pointer-events: all;
  /* border: 1pt solid red;
  background-image: url('/mock-up.png');
  background-position: 0px 0px;*/
  opacity: 1;
  transform: translateY(0%);
}

:deep(.group .iconify) {
  transition: transform 0.2s ease;
}

:deep(.group:hover .iconify) {
  transform: rotate(45deg);
}
</style>
